import type { TaskLabelView } from '@/lib/api/generated/model';

import { HiddenLabelsCount } from './HiddenLabelsCount';
import { LabelChip } from './LabelChip';

export interface LabelChipRowProps {
  labels: readonly TaskLabelView[];
  /** How many chips to show before collapsing the rest into a count. */
  max: number;
}

/**
 * A task's labels as a card shows them: the first `max`, then a count.
 *
 * The cap is a layout fact, not a data rule — a task may carry any number of
 * labels, but a card that becomes a chip gallery stops being scannable, which is
 * the only reason chips were put on it.
 *
 * Order is the contract's: `TaskView.labels` arrives sorted by name, and
 * re-sorting here would be a second ordering rule that could drift from it — the
 * visible consequence being that the two chips shown change between polls.
 *
 * Renders nothing when there are no labels. An empty row would reserve space on
 * every unlabelled card for something that is not there.
 */
export function LabelChipRow({ labels, max }: LabelChipRowProps) {
  if (labels.length === 0) return null;

  const shown = labels.slice(0, max);
  const hidden = labels.slice(max);

  return (
    <div className="flex flex-wrap items-center gap-1">
      {shown.map((label) => (
        <LabelChip key={label.id} label={label} />
      ))}
      <HiddenLabelsCount labels={hidden} />
    </div>
  );
}
